import { createNamespacedHelpers } from 'vuex'
import { isArray, isFunction, isObject } from 'lodash'
import { axiosInstance } from '@/services/api'

const { mapGetters: mapVersionGetters } = createNamespacedHelpers('projects/versions')

const assemblyPathAndIndex = ({ categories, assembly }) => {
  for (let i = 0; i < categories.length; i += 1) {
    const category = categories[i]
    for (let j = 0; j < category?.assemblies?.length; j += 1) {
      if (category.assemblies[j]._id === assembly._id) {
        return {
          path: category?.assemblies,
          index: j
        }
      }
    }
  }
}

const getElementById = ({ categories, id }) => {
  // Recursive function with Lodash
  function deepFind (obj, parent, offset, path = []) {
    // Check if the current object has the ID
    if (obj && obj._id === id) {
      return { item: obj, parent, offset, path }
    }
    if (obj === null || obj === undefined) {
      return null
    }
    // If it's an array, search each element
    if (isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        const result = deepFind(obj[i], obj, i, [...path, i])
        if (result) return result
      }
    } else if (isObject(obj) && !isFunction(obj)) {
      // If it's an object, search each property
      for (const key of Object.keys(obj)) {
        const result = deepFind(obj[key], obj, null, [...path, key])
        if (result) return result
      }
    }
    return null
  }

  const found = deepFind(categories)
  return found
}

export default {
  namespaced: true,
  state: {
    categories: {
      material: [],
      plant: [],
      operation: []
    }
  },
  getters: {
    ...mapVersionGetters(['version']),
    categories: state => state.categories
  },
  mutations: {
    removeAssemblyFromCategory (state, { type, assembly }) {
      const maybePath = assemblyPathAndIndex({
        categories: state.categories[assembly.type], assembly
      })
      if (maybePath) {
        maybePath.path.splice(maybePath.index, 1)
      }
    },
    removeElement (state, { element }) {
      const result = getElementById({ categories: state.categories, id: element._id })
      if (result) {
        result.parent.splice(result.offset, 1)
      }
    },
    setElement (state, { element, updatedElement }) {
      const result = getElementById({ categories: state.categories, id: element._id })
      if (result) {
        result.parent[result.offset] = {
          ...result.item,
          ...updatedElement
        }
      }
    },
    setElementOptions (state, { element, updatedElement }) {
      const result = getElementById({ categories: state.categories, id: element._id })
      Object.entries(updatedElement).forEach(([key, value]) => {
        result.item[key] = value
      })
    },
    setElementDimensionValue (state, { layout, dimension, value }) {
      layout[dimension].value = value
    },
    setAssembly (state, { replace, assembly }) {
      const maybePath = assemblyPathAndIndex({
        categories: state.categories[assembly.type], assembly
      })
      if (maybePath) {
        if (replace) {
          maybePath.path[maybePath.index] = assembly
        } else {
          maybePath.path[maybePath.index] = { ...maybePath.path[maybePath.index], ...assembly }
        }
      }
    },
    setCategories (state, { type, categories }) {
      state.categories[type] = categories
    }
  },
  actions: {
    async deleteAssembly ({ state, dispatch, commit }, { type, assembly }) {
      await axiosInstance.delete(`/assemblies/${assembly._id}`)
      commit('removeAssemblyFromCategory', { type, assembly })
      dispatch('showSnackbar', `Successfully deleted Assembly "${assembly.name}"`, { root: true })
    },
    async deleteElement ({ state, dispatch, commit }, { element }) {
      await axiosInstance.delete(`/elements/${element._id}`)
      commit('removeElement', { element })
      dispatch('showSnackbar', `Successfully deleted Element "${element.name}"`, { root: true })
    },
    async updateElementDimensionValue ({ state, dispatch, commit, rootGetters }, { element, layout, dimension, value }) {
      const results = getElementById({ categories: state.categories, id: element._id })
      if (results) {
        // check to see if there are missing dimensions.
        // in this case just update the store and don't save the value
        // to the database
        const missingDimensions = results.item.layouts[layout].dimensions
          .filter((d, i) => i !== dimension && !d.value && !d.zeroOk)

        const systemOfMeasurement = rootGetters.systemOfMeasurement
        const payload = { layout, dimension, value, system: systemOfMeasurement }
        const newStats = await axiosInstance.put(`/elements/${element._id}/value`, payload)
        const updatedElement = {
          ...newStats,
          layouts: { ...results.item.layouts }
        }
        commit('setElement', {
          element: results.item,
          updatedElement
        })
        commit('setElementDimensionValue', {
          layout: results.item.layouts[layout].dimensions,
          dimension,
          value: +value
        })
        return { results: results.item, validDimension: missingDimensions.length === 0 }
      }
    },
    async updateElementLayout ({ state, dispatch, commit }, { element, index }) {
      const results = getElementById({ categories: state.categories, id: element._id })
      if (results) {
        const { data } = await axiosInstance.put(`/elements/${element._id}/layout`, { index })
        const { quantity, co2 } = data
        commit('setElement', {
          element: results.item,
          updatedElement: {
            totalQuantity: quantity,
            totalCo2Kg: co2,
            activeLayout: index
          }
        })
        return results.item
      }
    },
    async updateElementOptions ({ state, commit }, { element, options }) {
      const results = getElementById({ categories: state.categories, id: element._id })
      if (results) {
        const missingDimensions = results.item.layouts[results.item.activeLayout].dimensions
          .filter((d) => !d.value && !d.zeroOk)
        commit('setElementOptions', { element, updatedElement: options })
        await axiosInstance.put(`/elements/${element._id}`, options)
        return { results: results.item, validDimension: missingDimensions.length === 0 }
      }
    },
    async updateAssembly ({ state, commit }, { updatedAssembly, assembly }) {
      await axiosInstance.put(`/assemblies/${assembly._id}`, updatedAssembly)
      commit('setAssembly', { type: assembly.type, assembly })
    },
    async updateElementQuantities ({ state, commit, getters, rootGetters }, { element }) {
      const results = getElementById({ categories: state.categories, id: element._id })
      if (results) {
        const systemOfMeasurement = rootGetters.systemOfMeasurement
        const { data: { element: newQuantities } } = await axiosInstance.get(`/elements/${element._id}`, {
          params: {
            select: 'totalQuantity emissionsCo2Kg shippingCo2Kg totalCo2Kg operationalCo2Kg storedCo2Kg totalWaterUse totalWaterUseCO2Impact',
            system: systemOfMeasurement
          }
        })
        if (newQuantities) {
          commit('setElement', { element, updatedElement: newQuantities })
          return results.item
        }
      }
      return null
    },
    async updateVersionQuantities ({ state, commit }, { versionId, type, typeForFilters, system }) {
      const { data: newElementData } = await axiosInstance.get(`/versions/${versionId}/elements`, {
        params: {
          type: typeForFilters,
          system,
          select: 'totalQuantity totalCo2Kg shippingCo2Kg emissionsCo2Kg storedCo2Kg operationalCo2Kg totalWaterUse totalWaterUseCO2Impact layouts'
        }
      })
      // updating existing elements with the new totalQuantity and totalCo2Kg field values
      const categories = state.categories[type].map(({ elements, ...category }) => ({
        ...category,
        elements: elements.map(existingElement => {
          const newElement = newElementData.find(element => element._id === existingElement._id)
          return {
            ...existingElement,
            ...newElement
          }
        })
      }))
      commit('setCategories', { categories, type })
    },
    async fetchCategories ({ commit }, { type, typesForFilters, system, version }) {
      // payload must {'type', 'system'}
      const { data: categories } = await axiosInstance.get(`/versions/${version._id}/categories`, {
        params: { type: typesForFilters, system }
      })

      // add installation of trees impact manually
      if (type === 'operation' && version.stats.other?.installationOfTreesImpactInTonnes > 0) {
        categories.push({
          name: 'Installation of new trees',
          meta: {
            installationOfTreesImpactInTonnes: Math.round(version.stats.other.installationOfTreesImpactInTonnes * 1000)
          }
        })
      }
      // installation of natural forest impact
      if (type === 'operation' && version.stats.other?.installationOfForestImpactInTonnes > 0) {
        categories.push({
          name: 'Installation of natural forest',
          meta: {
            installationOfForestImpactInTonnes: Math.round(version.stats.other.installationOfForestImpactInTonnes * 1000)
          }
        })
      }

      commit('setCategories', { categories, type })
    }
  }
}
